import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { Button } from '@leadsales/design-system/react';
import moment from 'moment';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { hasApprovedUtilityTemplate } from 'src/components/Chat/components/MessageContainer/components/EventBubble/components/MessageBubble/components/MetaErrorNotice/MetaErrorNotice.functions';
import { useLoadedFeatureFlag } from 'src/hooks/featureFlags/useLoadedFeatureFlag.hook';
import { useWhatsappApiContext } from 'src/pages/WhatsappApi/WhatsappApi.context';
import { META_ERROR_NOTICE_NAMESPACE } from 'src/utils/metaErrorNotice';
import {
  CONVERSATION_WINDOW_MS,
  describeWhen,
  firstSlotAfter,
} from './SchedulingRiskWarning.functions';
import type {
  tSchedulingContent,
  tSchedulingRisk,
  tWhen,
} from './SchedulingRiskWarning.types';
import useSchedulingRisk from './useSchedulingRisk.hook';
import './SchedulingRiskWarning.styles.scss';

export type tSchedulingRiskWarningProps = {
  triggerDate: string | undefined;
  triggerTime: string | undefined;
  content: tSchedulingContent;
  /** The modal's own save, as chosen. */
  onScheduleAnyway: () => void;
  /** Whether the modal's own save is disabled right now. */
  isScheduleDisabled: boolean;
  /** Moves the modal's fields ("YYYY-MM-DD", "HH:mm"). Offers "Programar después de…". */
  onChangeTrigger?: (next: { date: string; time: string }) => void;
  /** Switches the modal to scheduling a template. Offers "Usar una plantilla". */
  onUseTemplate?: () => void;
  /** Opens the template picker on Seguimiento. Offers "Usar una plantilla de seguimiento". */
  onUseUtilityTemplate?: () => void;
};

type tWhenPrefix = 'at' | 'on' | 'before' | 'after';

/**
 * "hoy 18:40", "el 15 sep", "antes de mañana 18:40", "después del 30 sep
 * 09:00": one key per prefix and kind, so every locale builds its own grammar.
 * A before/after never falls on yesterday (the hold is in the future) and an
 * "on" never on tomorrow (the 131049 is in the past); both read as a date.
 */
function whenPhrase(
  t: TFunction,
  options: { prefix: tWhenPrefix; when: tWhen },
): string {
  const { prefix, when } = options;
  const months = t('scheduledMessage.months', { returnObjects: true });
  const month = Array.isArray(months) ? String(months[when.monthIndex] ?? '') : '';
  const date = `${when.day} ${month}${when.year ? ` ${when.year}` : ''}`;
  const unusable =
    ((prefix === 'before' || prefix === 'after') && when.kind === 'yesterday') ||
    (prefix === 'on' && when.kind === 'tomorrow');
  const kind = unusable ? 'date' : when.kind;

  return t(`schedulingRisk.when.${prefix}_${kind}`, { time: when.time, date });
}

type tWarningCopy = {
  title: string;
  body: string;
};

function warningCopy(
  t: TFunction,
  options: { risk: tSchedulingRisk; lead: string; triggerAt: Date; now: Date },
): tWarningCopy {
  const { risk, lead, triggerAt, now } = options;
  const phrase = (prefix: tWhenPrefix, date: Date) =>
    whenPhrase(t, { prefix, when: describeWhen(date, now) });

  const engagementCap = (capAt: Date | null, repliedSinceCap: boolean) => {
    if (!capAt) return t('schedulingRisk.bodies.engagementCapUndated', { lead });
    return t(
      repliedSinceCap
        ? 'schedulingRisk.bodies.engagementCapReplied'
        : 'schedulingRisk.bodies.engagementCap',
      { lead, when: phrase('on', capAt) },
    );
  };

  switch (risk.rule) {
    case 'optedOut':
      return {
        title: t('schedulingRisk.titles.optedOut'),
        body: t('schedulingRisk.bodies.optedOut', { lead }),
      };
    case 'marketingHold':
      return {
        title: t('schedulingRisk.titles.likelyNotDelivered'),
        body: `${engagementCap(risk.capAt, risk.repliedSinceCap)} ${t(
          'schedulingRisk.bodies.hold',
          { beforeHoldEnd: phrase('before', risk.holdUntil) },
        )}`,
      };
    case 'experiment':
      return {
        title: t('schedulingRisk.titles.likelyNotDelivered'),
        body: t('schedulingRisk.bodies.experiment', { lead }),
      };
    case 'recentCap':
      return {
        title: t('schedulingRisk.titles.likelyNotDelivered'),
        body: engagementCap(risk.capAt, false),
      };
    case 'windowClosed': {
      const sendAt = phrase('at', triggerAt);
      const closesAt = risk.windowExpiresAt;
      if (!closesAt) {
        return {
          title: t('schedulingRisk.titles.windowClosed'),
          body: t('schedulingRisk.bodies.noOpenWindow', { lead, sendAt }),
        };
      }
      const lastMessage = phrase(
        'at',
        new Date(closesAt.getTime() - CONVERSATION_WINDOW_MS),
      );
      return {
        title: t('schedulingRisk.titles.windowClosed'),
        body:
          closesAt.getTime() > now.getTime()
            ? t('schedulingRisk.bodies.windowClosesBeforeSend', {
                lead,
                lastMessage,
                closes: phrase('at', closesAt),
                sendAt,
              })
            : t('schedulingRisk.bodies.windowAlreadyClosed', {
                lead,
                lastMessage,
                sendAt,
              }),
      };
    }
    default:
      return { title: '', body: '' };
  }
}

function SchedulingRiskWarningContent(props: tSchedulingRiskWarningProps) {
  const {
    triggerDate,
    triggerTime,
    content,
    onScheduleAnyway,
    isScheduleDisabled,
    onChangeTrigger,
    onUseTemplate,
    onUseUtilityTemplate,
  } = props;
  const { t } = useTranslation(['chat/chat', META_ERROR_NOTICE_NAMESPACE]);
  const { templates, templatesHydrated } = useWhatsappApiContext();
  const { risk, triggerAt, leadName } = useSchedulingRisk({
    triggerDate,
    triggerTime,
    content,
  });

  if (!risk || !triggerAt) return null;

  const now = new Date();
  const lead = leadName ?? t('schedulingRisk.thisLead');
  const copy = warningCopy(t, { risk, lead, triggerAt, now });

  const holdSlot = risk.rule === 'marketingHold' ? firstSlotAfter(risk.holdUntil) : null;
  const offersLaterSlot = Boolean(holdSlot && onChangeTrigger);
  const offersTemplate = risk.rule === 'windowClosed' && Boolean(onUseTemplate);
  // D5: only when a follow-up template exists, and never before the catalog
  // has loaded (until then "none approved" only means "not loaded yet").
  const offersUtilityTemplate =
    risk.rule === 'optedOut' &&
    Boolean(onUseUtilityTemplate) &&
    templatesHydrated &&
    hasApprovedUtilityTemplate(templates);

  return (
    <div
      className="SchedulingRiskWarning"
      role="status"
      data-testid="scheduling-risk-warning"
      data-rule={risk.rule}
    >
      <p className="SchedulingRiskWarning__Title">
        <ExclamationTriangleIcon
          className="SchedulingRiskWarning__Icon"
          aria-hidden="true"
        />
        <span>{copy.title}</span>
      </p>
      <p className="SchedulingRiskWarning__Body">{copy.body}</p>
      {offersUtilityTemplate ? (
        <p className="SchedulingRiskWarning__Note">
          {t('notes.utilityTemplateUse', { ns: META_ERROR_NOTICE_NAMESPACE })}
        </p>
      ) : null}
      <div className="SchedulingRiskWarning__Actions">
        {offersLaterSlot && holdSlot && risk.rule === 'marketingHold' ? (
          <Button
            type="button"
            color="secondary"
            size="xs"
            onClick={() =>
              onChangeTrigger?.({
                date: moment(holdSlot).format('YYYY-MM-DD'),
                time: moment(holdSlot).format('HH:mm'),
              })
            }
          >
            {t('schedulingRisk.actions.scheduleAfter', {
              afterHoldEnd: whenPhrase(t, {
                prefix: 'after',
                when: describeWhen(risk.holdUntil, now),
              }),
            })}
          </Button>
        ) : null}
        {offersTemplate ? (
          <Button type="button" color="secondary" size="xs" onClick={onUseTemplate}>
            {t('schedulingRisk.actions.useTemplate')}
          </Button>
        ) : null}
        {offersUtilityTemplate ? (
          <Button
            type="button"
            color="secondary"
            size="xs"
            onClick={onUseUtilityTemplate}
          >
            {t('schedulingRisk.actions.useUtilityTemplate')}
          </Button>
        ) : null}
        <Button
          type="button"
          color="ghost"
          size="xs"
          onClick={onScheduleAnyway}
          disabled={isScheduleDisabled}
        >
          {t('schedulingRisk.actions.scheduleAnyway')}
        </Button>
      </div>
    </div>
  );
}

/**
 * INT-1500 U13 (D6, D16): warns inside the schedule modal, before saving, when
 * Meta is unlikely to deliver what the seller is scheduling (or Leadsales will
 * not send it). Never blocks: the modal's own save keeps working, and
 * "Programar de todos modos" is that same save. Absent while
 * `chat_scheduling-risk-warning` is off: no request, no render.
 */
export default function SchedulingRiskWarning(props: tSchedulingRiskWarningProps) {
  const isEnabled = useLoadedFeatureFlag('chat_scheduling-risk-warning');

  return isEnabled ? <SchedulingRiskWarningContent {...props} /> : null;
}
